import RBush from 'rbush';
import { BBox } from 'rbush';
import { useCallback, useEffect, useState } from 'react';

import { toDocumentId, toFileId, useGetDocument, useGetFile } from '@/hooks/api';
import { useDocumentStore } from '@/store';

export type OcrItem = {
  page: number;
  rotation: number;
  blockType: 'line' | 'word';
  confidence: number;
  text: string;
  polygon: number[][];
  id: string;
  relationships: { type: string; ids: string[] }[];
};

const minX = (ocrItem: OcrItem) => Math.min(...ocrItem.polygon.map((point) => point[0]));
const minY = (ocrItem: OcrItem) => Math.min(...ocrItem.polygon.map((point) => point[1]));
const maxX = (ocrItem: OcrItem) => Math.max(...ocrItem.polygon.map((point) => point[0]));
const maxY = (ocrItem: OcrItem) => Math.max(...ocrItem.polygon.map((point) => point[1]));

class OcrTree extends RBush<OcrItem> {
  toBBox(ocrItem: OcrItem) {
    return { minX: minX(ocrItem), minY: minY(ocrItem), maxX: maxX(ocrItem), maxY: maxY(ocrItem) };
  }

  compareMinX(a: OcrItem, b: OcrItem) {
    return minX(a) - minX(b);
  }

  compareMinY(a: OcrItem, b: OcrItem) {
    return minY(a) - minY(b);
  }
}

export type UseOcrTreeOpts = {
  documentId?: string | null;
  page?: number;
};

export const useOcrTree = ({ page }: UseOcrTreeOpts) => {
  const documentId = useDocumentStore((state) => state.documentId);
  const { data: doc } = useGetDocument(toDocumentId(documentId));
  const { data: file } = useGetFile(toFileId(doc?.ocrFileUrl), {
    refetchInterval: (query) => (!query.state.data ? 2000 : undefined),
  });
  const [wordOcrTree, setWordOcrTree] = useState<OcrTree | null>(null);
  const [lineOcrTree, setLineOcrTree] = useState<OcrTree | null>(null);

  useEffect(() => {
    file?.text().then((data) => {
      const ocrItems = JSON.parse(data) as OcrItem[];

      const wordTree = new OcrTree();
      wordTree.load(ocrItems.filter((item) => item.blockType === 'word' && item.page === (page ?? item.page)));
      setWordOcrTree(wordTree);

      const lineTree = new OcrTree();
      lineTree.load(ocrItems.filter((item) => item.blockType === 'line' && item.page === (page ?? item.page)));
      setLineOcrTree(lineTree);
    });
  }, [file, page]);

  const search = useCallback(
    (bbox: BBox, type: OcrItem['blockType']) => {
      switch (type) {
        case 'line':
          return lineOcrTree?.search(bbox) ?? [];
        case 'word':
          return wordOcrTree?.search(bbox) ?? [];
        default:
          return [];
      }
    },
    [wordOcrTree, lineOcrTree]
  );

  return { search };
};
